/**
 * EventStuckPage - Eventos Travados
 * 
 * Página para gerenciar eventos com problemas
 */

import { type FC, useEffect, useState } from 'react';
import StandardPage from '@/components/Common/StandardPage';
import DataTable from '@/components/DataTable/DataTable';
import { eventColumns } from '@/components/DataTable/eventColumns';
import { eventService } from '@/services';
import type { EventDetailsResponse, EventDetailsFilters, PageResponse } from '@/types';
import styles from './EventsPage.module.css';

export const EventStuckPage: FC = () => {
  const [events, setEvents] = useState<EventDetailsResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isReprocessing, setIsReprocessing] = useState(false);

  useEffect(() => {
    loadStuckEvents();
  }, []);

  const loadStuckEvents = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const filters: EventDetailsFilters = {
        status: 'STUCK' as any,
        page: 0,
        size: 100,
        sort: 'createdAt,asc',
      };

      const response: PageResponse<EventDetailsResponse> = await eventService.getEventDetails(filters);
      setEvents(response.content);
    } catch (err) {
      setError('Erro ao carregar eventos travados');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReprocessAll = async () => {
    try {
      setIsReprocessing(true);
      await eventService.reprocessEvents({
        eventIds: events.map(e => e.id),
      });
      await loadStuckEvents();
      alert('Eventos reprocessados com sucesso!');
    } catch (err) {
      setError('Erro ao reprocessar eventos');
      console.error(err);
    } finally {
      setIsReprocessing(false);
    }
  };

  return (
    <StandardPage
      header={{
        title: "Eventos Travados",
        subtitle: `${events.length} eventos com problemas`,
        actions: events.length > 0 ? [{
          label: "Reprocessar Todos",
          onClick: handleReprocessAll,
          disabled: isReprocessing,
        }] : [],
      }}
      error={error ? new Error(error) : null}
    >
      <div className={styles.tableContainer}>
        {events.length === 0 && !isLoading ? (
          <div className={styles.emptyState}>
            <p>✅ Nenhum evento travado encontrado!</p>
          </div>
        ) : (
          <DataTable
            data={events}
            columns={eventColumns}
            isLoading={isLoading}
            emptyMessage="Nenhum evento travado"
            enablePagination={false}
          />
        )}
      </div>
    </StandardPage>
  );
};

export default EventStuckPage;
